import * as THREE from 'three';
import type { CommandHistory } from '@/core/CommandHistory';
import type { Terrain } from '@/terrain/Terrain';
import { TerrainRaycaster } from '@/terrain/TerrainRaycaster';
import {
  clampBrushRadius,
  clampUnit,
  DEFAULT_BRUSH,
  holdDabCount,
  sampleStrokeSegment,
  type BrushPoint,
  type BrushShape,
} from '@/terrain/TerrainBrush';
import {
  applyPaintDab,
  PaintStrokeState,
  type PaintMode,
} from '@/terrain/TerrainPaint';
import {
  applySculptDab,
  sculptLimits,
  SCULPT_MODE_COLORS,
  SCULPT_MODES,
  type SculptMode,
} from '@/terrain/TerrainSculpt';
import {
  BASE_SURFACE_LAYER,
  EMPTY_RECT,
  findSurfaceLayer,
  isEmptyRect,
  surfaceLayers,
  surfacePalette,
  unionRect,
  type SurfaceLayer,
  type SurfaceLayerId,
  type SurfaceRect,
} from '@/terrain/TerrainSurface';
import { PaintStrokeCommand, SculptStrokeCommand } from './TerrainCommands';
import { TerrainBrushCursor } from './TerrainBrushCursor';

/**
 * The ground tools: paint and sculpt (milestone 5.6 / spec §6.4).
 *
 * ## One engine, two effects
 *
 * Painting and sculpting are the same gesture — press, drag over ground, release — differing only in
 * what a dab does when it lands. So there is one stroke engine here and two appliers, rather than two
 * tools that each re-derive pointer capture, dab spacing, dirty-rect tracking and undo. Everything
 * subtle lives in one place and both tools get it.
 *
 * ## The input model
 *
 * Navigation owns the middle button and the wheel (`CameraController`), so the left button is free
 * for the brush and no modal "now you are painting, navigation is off" state is needed: you can
 * orbit mid-stroke and keep going. That is worth more than it sounds — grading a
 * bank means constantly changing your view of it.
 *
 * | Input | Effect |
 * | --- | --- |
 * | Left-drag | Apply the brush |
 * | Middle-drag, `Shift` + middle-drag, wheel | Orbit / pan / zoom, unchanged |
 * | `Ctrl` + wheel | Brush radius |
 * | `Shift` + wheel | Brush strength (flow, or metres per dab) |
 * | `[` / `]` | Brush radius, keyboard |
 * | `Shift` + `[` / `]` | Brush strength, keyboard |
 * | `-` / `=` | Brush hardness |
 * | `,` / `.` | Previous / next layer (paint) or mode (sculpt) |
 * | Hold `Alt` | Inverse: erase to grass, or lower instead of raise |
 * | Hold `Shift` | Smooth: feather a painted edge, or relax a lumpy grade |
 * | `Esc` | Back to the select tool |
 *
 * `Ctrl`/`Shift` + wheel rather than a bare wheel because a bare wheel is zoom, and taking zoom away
 * from a user who is leaning into a slope to look at it would be a bad trade for a size control that
 * has a keyboard equivalent anyway. The wheel listener sits on `window` in the capture phase so it
 * gets first refusal ahead of the camera's own — modifier held, the camera never sees the event.
 *
 * `Alt` and `Shift` are *held*, not toggled, because both are corrections you make continuously
 * while looking at the result — smoothing an edge you just drew, backing off a hump you just raised.
 * A mode you have to switch into and out of turns a two-second correction into four clicks.
 */

export type TerrainToolKind = 'paint' | 'sculpt';

export interface TerrainEditorDeps {
  domElement: HTMLElement;
  getCamera: () => THREE.Camera;
  terrain: () => Terrain | null;
  /** Shared with the flora editor, so Ctrl+Z walks one timeline across the whole site. */
  history: CommandHistory;
  /** Called after the ground's *heights* change, with the affected vertex rect. */
  onGroundChanged?: (rect: SurfaceRect) => void;
  /** Called whenever tool state changes, so the HUD can re-read it. */
  onChange?: () => void;
  /** Called when the tool takes itself out of service (Esc). */
  onExit?: () => void;
  /**
   * Called the first time and every time one of the brush's advanced commands is used — a modifier
   * wheel, a size or strength key, a held Alt or Shift. The HUD listens so it can take down the
   * coachmark that was teaching them; nothing about the tool depends on it.
   */
  onAdvancedCommand?: () => void;
  /** Optional element for the tool readout. */
  readout?: HTMLElement;
}

export interface PaintSettings {
  layer: SurfaceLayerId;
  brush: BrushShape;
}

export interface SculptSettings {
  mode: SculptMode;
  brush: BrushShape;
  /** Metres a dab moves the ground at full falloff, before `brush.flow` scales it. */
  strengthMeters: number;
}

export const MIN_SCULPT_STRENGTH = 0.005;
export const MAX_SCULPT_STRENGTH = 0.25;

const UP = new THREE.Vector3(0, 1, 0);

/** Ring colours that are not any layer's colour, so a modifier is never mistaken for a material. */
const ERASE_COLOR = 0xf3f0e4;
/** Blending paint borrows the sculpt smoother's blue: it is the same idea done to a different grid. */
const SMOOTH_COLOR = SCULPT_MODE_COLORS.smooth;

export class TerrainEditor {
  readonly paint: PaintSettings = {
    layer: 'mulch',
    brush: { ...DEFAULT_BRUSH, flow: 0.35, opacity: 1, hardness: 0.5 },
  };
  readonly sculpt: SculptSettings = {
    mode: 'raise',
    brush: { ...DEFAULT_BRUSH, flow: 1, opacity: 1, hardness: 0.35, radiusMeters: 3 },
    strengthMeters: 0.03,
  };

  private readonly raycaster = new TerrainRaycaster();
  private readonly cursor = new TerrainBrushCursor();
  private readonly plane = new THREE.Plane();
  private readonly rayHit = new THREE.Vector3();
  private readonly screenRay = new THREE.Raycaster();
  private readonly localPoint = new THREE.Vector2();
  private readonly ndc = new THREE.Vector2();

  private tool: TerrainToolKind | null = null;
  private altHeld = false;
  private shiftHeld = false;
  /** What the last dab would have done, so a change of it can be reported once rather than per event. */
  private lastEffectiveMode = '';

  /* Stroke state. Everything here is null/zero between strokes. */
  private pressed = false;
  private pointerId: number | null = null;
  private hoverPoint: BrushPoint | null = null;
  private lastDabPoint: BrushPoint | null = null;
  private carryMeters = 0;
  private holdSeconds = 0;
  private strokeRect: SurfaceRect = { ...EMPTY_RECT };
  private strokePlaneHeight = 0;
  private paintStroke: PaintStrokeState | null = null;
  private paintBefore: Uint8Array | null = null;
  private sculptBefore: Float32Array | null = null;
  /**
   * The mode the stroke started in. Locked at press so that letting go of Alt halfway through a drag
   * does not turn the back half of one gesture into a different operation — and so the undo entry
   * can be honestly labelled.
   */
  private strokePaintMode: PaintMode = 'paint';
  private strokeSculptMode: SculptMode = 'raise';
  private flattenTarget = 0;
  private viewDirty: SurfaceRect = { ...EMPTY_RECT };
  private surfaceDirty = false;

  private readonly onPointerDown = (e: PointerEvent): void => this.handlePointerDown(e);
  private readonly onPointerMove = (e: PointerEvent): void => this.handlePointerMove(e);
  private readonly onPointerUp = (e: PointerEvent): void => this.handlePointerUp(e);
  private readonly onPointerLeave = (): void => this.handlePointerLeave();
  private readonly onKeyDown = (e: KeyboardEvent): void => this.handleKeyDown(e);
  private readonly onKeyUp = (e: KeyboardEvent): void => this.handleKeyUp(e);
  private readonly onWheel = (e: WheelEvent): void => this.handleWheel(e);

  private readonly unsubscribePalette: () => void;

  constructor(private readonly deps: TerrainEditorDeps) {
    // A retired material must not stay armed on the brush: the next dab would look up a channel
    // nothing owns and paint nothing, which reads as a broken tool rather than a removed material.
    this.unsubscribePalette = surfacePalette.onChange(() => {
      if (surfacePalette.has(this.paint.layer)) return;
      this.paint.layer = BASE_SURFACE_LAYER.id;
      this.updateCursorColor();
      this.updateReadout();
      this.deps.onChange?.();
    });
  }

  /** The active paint layer, or grass when it has been retired out from under the tool. */
  private get activeLayer(): SurfaceLayer {
    return findSurfaceLayer(this.paint.layer) ?? BASE_SURFACE_LAYER;
  }

  /** The brush cursor group — the caller adds this to the scene overlay root. */
  get overlayObject(): THREE.Object3D {
    return this.cursor.object;
  }

  get activeTool(): TerrainToolKind | null {
    return this.tool;
  }

  get isActive(): boolean {
    return this.tool !== null;
  }

  /** The brush the active tool is using, or the paint brush when idle. */
  get activeBrush(): BrushShape {
    return this.tool === 'sculpt' ? this.sculpt.brush : this.paint.brush;
  }

  /**
   * The sculpt mode a dab would use right now — the panel's choice, with any held modifier applied.
   *
   * Public because the pointer glyph is drawn from it: what the cursor shows and what the next dab
   * does have to be the same answer, and the only way to guarantee that is for them to be one.
   */
  get effectiveSculptMode(): SculptMode {
    return this.resolveSculptMode();
  }

  activate(tool: TerrainToolKind): void {
    if (this.tool === tool) return;
    if (this.tool === null) this.attachListeners();
    this.tool = tool;
    this.cancelStroke();
    this.cursor.setVisible(false);
    this.updateCursorColor();
    this.updateReadout();
    this.deps.onChange?.();
  }

  deactivate(): void {
    if (this.tool === null) return;
    this.commitStroke();
    this.tool = null;
    this.detachListeners();
    this.cursor.setVisible(false);
    this.hoverPoint = null;
    this.clearReadout();
    this.deps.onChange?.();
  }

  /* --------------------------------- Settings --------------------------------- */

  setPaintLayer(layer: SurfaceLayerId): void {
    if (!surfacePalette.has(layer)) return;
    this.paint.layer = layer;
    this.updateCursorColor();
    this.updateReadout();
    this.deps.onChange?.();
  }

  setSculptMode(mode: SculptMode): void {
    this.sculpt.mode = mode;
    this.updateCursorColor();
    this.updateReadout();
    this.deps.onChange?.();
  }

  setBrushRadius(meters: number): void {
    this.activeBrush.radiusMeters = clampBrushRadius(meters);
    this.refreshCursor();
    this.updateReadout();
    this.deps.onChange?.();
  }

  setBrushHardness(hardness: number): void {
    this.activeBrush.hardness = clampUnit(hardness, DEFAULT_BRUSH.hardness);
    this.refreshCursor();
    this.updateReadout();
    this.deps.onChange?.();
  }

  /**
   * The one strength control, whatever the tool means by strength — paint flow in [0,1], sculpt
   * metres per dab. One slider and one shortcut for both keeps the panel honest about the fact that
   * these are the same gesture with different consequences.
   */
  setBrushStrength(value: number): void {
    if (this.tool === 'sculpt') {
      const clamped = Math.min(MAX_SCULPT_STRENGTH, Math.max(MIN_SCULPT_STRENGTH, value));
      this.sculpt.strengthMeters = Number.isFinite(clamped) ? clamped : MIN_SCULPT_STRENGTH;
    } else {
      this.paint.brush.flow = clampUnit(value, DEFAULT_BRUSH.flow);
    }
    this.updateReadout();
    this.deps.onChange?.();
  }

  /** Current strength on the active tool's own scale. */
  get brushStrength(): number {
    return this.tool === 'sculpt' ? this.sculpt.strengthMeters : this.paint.brush.flow;
  }

  setPaintOpacity(value: number): void {
    this.paint.brush.opacity = clampUnit(value, 1);
    this.updateReadout();
    this.deps.onChange?.();
  }

  /* ---------------------------------- Input ---------------------------------- */

  private attachListeners(): void {
    const el = this.deps.domElement;
    el.addEventListener('pointerdown', this.onPointerDown);
    el.addEventListener('pointermove', this.onPointerMove);
    el.addEventListener('pointerup', this.onPointerUp);
    el.addEventListener('pointercancel', this.onPointerUp);
    el.addEventListener('pointerleave', this.onPointerLeave);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    // Capture phase on window: the camera's wheel listener sits on the canvas, so this one sees the
    // event first and can claim it when a modifier is held.
    window.addEventListener('wheel', this.onWheel, { capture: true, passive: false });
  }

  private detachListeners(): void {
    const el = this.deps.domElement;
    el.removeEventListener('pointerdown', this.onPointerDown);
    el.removeEventListener('pointermove', this.onPointerMove);
    el.removeEventListener('pointerup', this.onPointerUp);
    el.removeEventListener('pointercancel', this.onPointerUp);
    el.removeEventListener('pointerleave', this.onPointerLeave);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('wheel', this.onWheel, { capture: true });
  }

  private handlePointerDown(e: PointerEvent): void {
    if (this.tool === null || e.button !== 0) return;
    const point = this.pickGround(e, true);
    if (!point) return;

    this.readModifiers(e);
    this.deps.domElement.setPointerCapture(e.pointerId);
    this.pointerId = e.pointerId;
    this.pressed = true;
    this.carryMeters = 0;
    this.holdSeconds = 0;
    this.strokeRect = { ...EMPTY_RECT };
    this.beginStroke(point);
    // A click with no drag still has to do something, so the first dab lands immediately.
    this.applyDab(point);
    this.lastDabPoint = point;
    this.hoverPoint = point;
    e.preventDefault();
  }

  private handlePointerMove(e: PointerEvent): void {
    if (this.tool === null) return;
    this.readModifiers(e);
    const point = this.pickGround(e, false);
    if (!point) {
      if (!this.pressed) {
        this.cursor.setVisible(false);
        this.hoverPoint = null;
      }
      return;
    }
    this.hoverPoint = point;
    this.refreshCursor();
    this.updateReadout();
  }

  private handlePointerUp(e: PointerEvent): void {
    if (!this.pressed || (this.pointerId !== null && e.pointerId !== this.pointerId)) return;
    if (this.pointerId !== null && this.deps.domElement.hasPointerCapture(this.pointerId)) {
      this.deps.domElement.releasePointerCapture(this.pointerId);
    }
    this.pointerId = null;
    this.commitStroke();
  }

  private handlePointerLeave(): void {
    if (this.pressed) return;
    this.cursor.setVisible(false);
    this.hoverPoint = null;
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (this.tool === null) return;
    const target = e.target as HTMLElement | null;
    if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
    this.setModifiers(e.altKey, e.shiftKey);

    // Everything below is a bare or Shift-modified key; Ctrl/Cmd combinations belong to the editor.
    if (e.ctrlKey || e.metaKey) {
      this.updateCursorColor();
      return;
    }

    switch (e.key) {
      case '[':
      case '{':
        this.nudge(e.shiftKey, -1);
        break;
      case ']':
      case '}':
        this.nudge(e.shiftKey, 1);
        break;
      case '-':
      case '_':
        this.setBrushHardness(this.activeBrush.hardness - 0.1);
        break;
      case '=':
      case '+':
        this.setBrushHardness(this.activeBrush.hardness + 0.1);
        break;
      case ',':
        this.cycleChoice(-1);
        break;
      case '.':
        this.cycleChoice(1);
        break;
      case 'Escape':
        this.deps.onExit?.();
        return;
      default:
        this.updateCursorColor();
        this.updateReadout();
        return;
    }
    // Everything that reaches here is one of the brush's advanced commands rather than a stray key.
    this.deps.onAdvancedCommand?.();
    e.preventDefault();
  }

  private handleKeyUp(e: KeyboardEvent): void {
    if (this.tool === null) return;
    this.setModifiers(e.altKey, e.shiftKey);
    this.updateReadout();
  }

  /**
   * Record the held modifiers and repaint the ring for them.
   *
   * Taking hold of Alt or Shift counts as using an advanced command: they are two of the four
   * things the coachmark is there to teach, and having just used one is the clearest possible sign
   * that it is no longer needed.
   */
  private setModifiers(alt: boolean, shift: boolean): void {
    const gained = (alt && !this.altHeld) || (shift && !this.shiftHeld);
    this.altHeld = alt;
    this.shiftHeld = shift;
    this.updateCursorColor();
    if (gained) this.deps.onAdvancedCommand?.();
  }

  private handleWheel(e: WheelEvent): void {
    if (this.tool === null) return;
    const radius = e.ctrlKey || e.metaKey;
    const strength = e.shiftKey && !radius;
    if (!radius && !strength) return;
    // Claim it before the camera's listener runs, or the site zooms while the brush resizes.
    e.preventDefault();
    e.stopPropagation();
    // Multiplicative, so one notch is the same *proportional* change at 20 cm and at 20 m.
    const factor = Math.exp(-e.deltaY * 0.0015);
    if (radius) {
      this.setBrushRadius(this.activeBrush.radiusMeters * factor);
    } else {
      this.setBrushStrength(this.brushStrength * factor);
    }
    this.refreshCursor();
    this.deps.onAdvancedCommand?.();
  }

  private nudge(shift: boolean, direction: number): void {
    if (shift) {
      this.setBrushStrength(this.brushStrength * (direction > 0 ? 1.25 : 0.8));
    } else {
      this.setBrushRadius(this.activeBrush.radiusMeters * (direction > 0 ? 1.25 : 0.8));
    }
  }

  private cycleChoice(step: number): void {
    if (this.tool === 'sculpt') {
      const index = SCULPT_MODES.indexOf(this.sculpt.mode);
      const next = SCULPT_MODES[wrap(index + step, SCULPT_MODES.length)];
      if (next) this.setSculptMode(next);
      return;
    }
    const ids = surfaceLayers().map((entry) => entry.id);
    const index = ids.indexOf(this.paint.layer);
    const next = ids[wrap(index + step, ids.length)];
    if (next) this.setPaintLayer(next);
  }

  private readModifiers(e: PointerEvent): void {
    this.setModifiers(e.altKey, e.shiftKey);
  }

  /* ---------------------------------- Picking ---------------------------------- */

  /**
   * Pointer → a point on the ground, in terrain-local metres.
   *
   * The mesh raycast is the accurate answer and is tried first. When it misses — the brush has run
   * off the near edge of the site, or a sculpted pit has opened under an oblique view — the ray is
   * intersected with a horizontal plane at the height the stroke began instead. Without that
   * fallback a stroke stutters the instant the pointer crosses ground the ray no longer meets, which
   * is precisely when you are working on an edge.
   */
  private pickGround(e: PointerEvent, isStrokeStart: boolean): BrushPoint | null {
    const terrain = this.deps.terrain();
    if (!terrain) return null;
    const rect = this.deps.domElement.getBoundingClientRect();
    this.ndc.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );

    const hit = this.raycaster.intersect(this.ndc, this.deps.getCamera(), terrain.mesh);
    if (hit) {
      if (isStrokeStart) this.strokePlaneHeight = hit.y;
      terrain.toLocal(hit.x, hit.z, this.localPoint);
      return { x: this.localPoint.x, z: this.localPoint.y };
    }
    if (isStrokeStart) return null;

    this.screenRay.setFromCamera(this.ndc, this.deps.getCamera());
    this.plane.set(UP, -this.strokePlaneHeight);
    const planeHit = this.screenRay.ray.intersectPlane(this.plane, this.rayHit);
    if (!planeHit) return null;
    terrain.toLocal(planeHit.x, planeHit.z, this.localPoint);
    return { x: this.localPoint.x, z: this.localPoint.y };
  }

  /* ---------------------------------- Strokes ---------------------------------- */

  /**
   * Advance the active stroke. Driven by the application's frame loop rather than by pointer events,
   * so a held-still brush keeps working and a fast drag is resampled at a fixed spacing — the whole
   * point of `TerrainBrush`. Also the single place the view is refreshed, which batches a frame's
   * worth of dabs into one geometry or texture upload.
   */
  tick(deltaSeconds: number): void {
    if (this.tool !== null && this.pressed && this.hoverPoint) {
      const brush = this.activeBrush;
      const spacing = Math.max(0.02, brush.spacing * brush.radiusMeters);
      let emitted = 0;

      if (this.lastDabPoint) {
        const sampling = sampleStrokeSegment(
          this.lastDabPoint,
          this.hoverPoint,
          spacing,
          this.carryMeters,
        );
        for (const dab of sampling.dabs) this.applyDab(dab);
        emitted = sampling.dabs.length;
        this.carryMeters = sampling.carryMeters;
        this.lastDabPoint = this.hoverPoint;
      }

      if (emitted === 0) {
        // Not moving (or not moving far enough to owe a dab): pay out on the clock instead.
        this.holdSeconds += Math.max(0, deltaSeconds);
        const hold = holdDabCount(this.holdSeconds);
        this.holdSeconds = hold.remainderSeconds;
        for (let i = 0; i < hold.count; i += 1) this.applyDab(this.hoverPoint);
      } else {
        this.holdSeconds = 0;
      }
    }
    this.flushView();
  }

  private beginStroke(point: BrushPoint): void {
    const terrain = this.deps.terrain();
    if (!terrain) return;
    if (this.tool === 'paint') {
      this.strokePaintMode = this.effectivePaintMode();
      if (!this.paintStroke || this.paintStroke.applied.length !== terrain.surface.texelCount) {
        this.paintStroke = new PaintStrokeState(terrain.surface.texelCount);
      }
      this.paintStroke.reset();
      // The stroke's dirty rectangle is not known until it ends, so the whole grid is held for the
      // duration and the final rectangle is cut out of it at commit. Transient, not stored.
      this.paintBefore = terrain.surface.data.slice();
    } else {
      this.sculptBefore = terrain.field.heights.slice();
      this.strokeSculptMode = this.resolveSculptMode();
      // Flatten levels to where the stroke began — you choose the height by choosing where to press.
      this.flattenTarget = terrain.sampleHeight(point.x, point.z);
    }
  }

  private applyDab(point: BrushPoint): void {
    const terrain = this.deps.terrain();
    if (!terrain) return;

    if (this.tool === 'paint') {
      const rect = applyPaintDab(terrain.surface, point, this.paint.brush, {
        layer: this.paint.layer,
        mode: this.strokePaintMode,
        ...(this.paintStroke ? { stroke: this.paintStroke } : {}),
      });
      if (isEmptyRect(rect)) return;
      this.strokeRect = unionRect(this.strokeRect, rect);
      this.surfaceDirty = true;
      return;
    }

    const limits = sculptLimits(terrain.params.elevationAmplitude);
    const rect = applySculptDab(terrain.field, point, this.sculpt.brush, {
      mode: this.strokeSculptMode,
      strengthMeters: this.sculpt.strengthMeters,
      targetHeight: this.flattenTarget,
      minHeight: limits.minHeight,
      maxHeight: limits.maxHeight,
    });
    if (isEmptyRect(rect)) return;
    this.strokeRect = unionRect(this.strokeRect, rect);
    this.viewDirty = unionRect(this.viewDirty, rect);
  }

  /** Push a frame's accumulated edits into the view, once. */
  private flushView(): void {
    const terrain = this.deps.terrain();
    if (!terrain) return;
    if (this.surfaceDirty) {
      terrain.applySurfaceRegion();
      this.surfaceDirty = false;
    }
    if (!isEmptyRect(this.viewDirty)) {
      terrain.applyHeightRegion(this.viewDirty);
      this.deps.onGroundChanged?.(this.viewDirty);
      this.viewDirty = { ...EMPTY_RECT };
    }
  }

  /**
   * End the stroke and record it. The undo entry holds only the rectangle the stroke actually
   * touched — see `TerrainCommands` — and a stroke that changed nothing (a click on ground the brush
   * could not reach) records nothing at all, rather than leaving a no-op in the history.
   */
  private commitStroke(): void {
    if (!this.pressed) return;
    this.pressed = false;
    this.lastDabPoint = null;
    this.carryMeters = 0;
    this.holdSeconds = 0;
    this.flushView();

    const terrain = this.deps.terrain();
    const rect = this.strokeRect;
    this.strokeRect = { ...EMPTY_RECT };
    if (!terrain || isEmptyRect(rect)) {
      this.paintBefore = null;
      this.sculptBefore = null;
      return;
    }

    if (this.tool === 'paint' && this.paintBefore) {
      const before = cutBytes(this.paintBefore, terrain.surface.width, rect);
      const after = terrain.surface.readRect(rect);
      this.paintBefore = null;
      const layer = this.activeLayer;
      const label =
        this.strokePaintMode === 'erase'
          ? 'Erase ground paint'
          : this.strokePaintMode === 'smooth'
            ? 'Blend ground paint'
            : `Paint ${layer.name.toLowerCase()}`;
      const command = new PaintStrokeCommand(terrain, rect, before, after, label);
      if (!command.isEmpty) this.deps.history.push(command);
    } else if (this.tool === 'sculpt' && this.sculptBefore) {
      const before = cutHeights(this.sculptBefore, terrain.field.cols, rect);
      const after = cutHeights(terrain.field.heights, terrain.field.cols, rect);
      this.sculptBefore = null;
      const command = new SculptStrokeCommand(
        terrain,
        rect,
        before,
        after,
        SCULPT_LABELS[this.strokeSculptMode],
        (changed) => this.deps.onGroundChanged?.(changed),
      );
      if (!command.isEmpty) this.deps.history.push(command);
      terrain.finishHeightEdit();
    }

    this.deps.onChange?.();
  }

  /** Abandon an in-flight stroke without recording it (tool switch, teardown). */
  private cancelStroke(): void {
    this.pressed = false;
    this.pointerId = null;
    this.lastDabPoint = null;
    this.paintBefore = null;
    this.sculptBefore = null;
    this.strokeRect = { ...EMPTY_RECT };
  }

  /* ---------------------------------- Modes ---------------------------------- */

  private effectivePaintMode(): PaintMode {
    if (this.shiftHeld) return 'smooth';
    if (this.altHeld) return 'erase';
    return this.paint.layer === 'grass' ? 'erase' : 'paint';
  }

  private resolveSculptMode(): SculptMode {
    if (this.shiftHeld) return 'smooth';
    if (!this.altHeld) return this.sculpt.mode;
    if (this.sculpt.mode === 'raise') return 'lower';
    if (this.sculpt.mode === 'lower') return 'raise';
    return this.sculpt.mode;
  }

  /* ---------------------------------- Feedback ---------------------------------- */

  private refreshCursor(): void {
    const terrain = this.deps.terrain();
    if (!terrain || !this.hoverPoint || this.tool === null) {
      this.cursor.setVisible(false);
      return;
    }
    const brush = this.activeBrush;
    this.cursor.update(terrain, this.hoverPoint.x, this.hoverPoint.z, brush.radiusMeters, brush.hardness);
    this.cursor.setVisible(true);
  }

  private updateCursorColor(): void {
    if (this.tool === null) return;
    let mode: string;
    if (this.tool === 'paint') {
      const paintMode = this.effectivePaintMode();
      if (paintMode === 'smooth') this.cursor.setColor(SMOOTH_COLOR);
      else if (paintMode === 'erase') this.cursor.setColor(ERASE_COLOR);
      else this.cursor.setColor(this.activeLayer.color);
      mode = `paint:${paintMode}:${this.paint.layer}`;
    } else {
      const sculptMode = this.resolveSculptMode();
      this.cursor.setColor(SCULPT_MODE_COLORS[sculptMode]);
      mode = `sculpt:${sculptMode}`;
    }
    // A held Alt changes what the next dab does, so it has to change the pointer too — and the
    // pointer is the HUD's to draw. Reported only on an actual change, because this runs on every
    // key and pointer event the tool sees.
    if (mode !== this.lastEffectiveMode) {
      this.lastEffectiveMode = mode;
      this.deps.onChange?.();
    }
  }

  /** The status line the HUD shows while a ground tool is live. */
  describe(): string {
    if (this.tool === 'paint') {
      const mode = this.effectivePaintMode();
      const what =
        mode === 'smooth'
          ? 'Blend'
          : mode === 'erase'
            ? 'Erase to grass'
            : this.activeLayer.name;
      return (
        `${what} · brush ${this.paint.brush.radiusMeters.toFixed(1)} m · ` +
        `edge ${Math.round(this.paint.brush.hardness * 100)}% · ` +
        `flow ${Math.round(this.paint.brush.flow * 100)}%`
      );
    }
    if (this.tool === 'sculpt') {
      const mode = this.resolveSculptMode();
      return (
        `${SCULPT_LABELS[mode]} · brush ${this.sculpt.brush.radiusMeters.toFixed(1)} m · ` +
        `edge ${Math.round(this.sculpt.brush.hardness * 100)}% · ` +
        `${(this.sculpt.strengthMeters * 100).toFixed(1)} cm/dab`
      );
    }
    return '';
  }

  private updateReadout(): void {
    if (this.deps.readout) this.deps.readout.textContent = this.describe();
  }

  private clearReadout(): void {
    if (this.deps.readout) this.deps.readout.textContent = '';
  }

  dispose(): void {
    this.unsubscribePalette();
    this.deactivate();
    this.cursor.dispose();
  }
}

const SCULPT_LABELS: Record<SculptMode, string> = {
  raise: 'Raise',
  lower: 'Lower',
  smooth: 'Smooth',
  flatten: 'Flatten',
};

/** Human names for the sculpt modes, for the tool panel. */
export function sculptModeLabel(mode: SculptMode): string {
  return SCULPT_LABELS[mode];
}

/**
 * Layers the paint palette offers, base layer first — it doubles as the eraser.
 *
 * A function rather than a constant, because the palette can grow while the app is running: a
 * module-level array would have been snapshotted at import time and would never show a forged
 * material.
 */
export function paintPalette(): readonly SurfaceLayer[] {
  return surfaceLayers();
}

function wrap(value: number, length: number): number {
  return ((value % length) + length) % length;
}

/**
 * Cut a rectangle out of a full-grid RGBA snapshot. Used at commit time to turn the whole-grid
 * "before" copy into the small diff the history keeps.
 */
function cutBytes(source: Uint8Array, gridWidth: number, rect: SurfaceRect): Uint8Array {
  const out = new Uint8Array(rect.width * rect.height * 4);
  for (let row = 0; row < rect.height; row += 1) {
    const from = ((rect.y + row) * gridWidth + rect.x) * 4;
    out.set(source.subarray(from, from + rect.width * 4), row * rect.width * 4);
  }
  return out;
}

/** The heightfield equivalent of {@link cutBytes}. */
function cutHeights(source: Float32Array, gridWidth: number, rect: SurfaceRect): Float32Array {
  const out = new Float32Array(rect.width * rect.height);
  for (let row = 0; row < rect.height; row += 1) {
    const from = (rect.y + row) * gridWidth + rect.x;
    out.set(source.subarray(from, from + rect.width), row * rect.width);
  }
  return out;
}
